(() => {
  document.documentElement.classList.remove('no-js');
  const focusable = 'a[href],button,input,select,textarea,[tabindex]';
  const rootPath = window.Shopify?.routes?.root || '/';
  const variantState = window.MoveCalmlyVariantState;
  const productAddState = window.MoveCalmlyProductAddState;
  const productGallery = window.MoveCalmlyProductGallery;
  const cartPage = window.MoveCalmlyCartPage;
  const cartDrawerState = window.MoveCalmlyCartDrawerState;
  const headerRuntimeState = window.MoveCalmlyHeaderRuntimeState;
  const customerAddressState = window.MoveCalmlyCustomerAddressState;
  const navigationState = window.MoveCalmlyNavigationState;
  let activeDialog = null;
  let returnFocus = null;
  let productRequest = 0;
  let cartDrawerController = null;
  let cartDrawerRoot = null;
  let navigationController = navigationState?.init(document);

  function focusableItems(container) {
    if (!container) return [];
    return [...container.querySelectorAll(focusable)].filter((element) => {
      if (element.tabIndex < 0 || element.matches(':disabled')) return false;
      if (element.closest('[hidden],[inert],[aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
      return element.getClientRects().length > 0;
    });
  }

  function closeDialog(dialog, restoreFocus = true) {
    if (!dialog) return;
    dialog.dataset.open = 'false';
    dialog.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mc-scroll-lock');
    activeDialog = null;
    const focusTarget = returnFocus;
    returnFocus = null;
    if (restoreFocus && focusTarget?.isConnected) focusTarget.focus();
  }

  function openDialog(dialog, trigger) {
    if (!dialog) return;
    navigationController?.closeForDialog();
    returnFocus = trigger;
    activeDialog = dialog;
    dialog.dataset.open = 'true';
    dialog.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mc-scroll-lock');
    const panel = dialog.querySelector('.mc-dialog__panel');
    const items = focusableItems(panel);
    const preferred = items.find((element) => element.matches('[data-dialog-close]'));
    const target = preferred || items[0];
    if (target) target.focus();
  }

  function setTextStatus(target, message, isError = false) {
    if (!target) return;
    target.textContent = message;
    target.classList.toggle('mc-error', isError);
  }

  function announce(message) {
    const region = document.getElementById('McLiveRegion');
    if (region) region.textContent = message;
  }

  function setButtonLabel(button, label, busy = false) {
    button.replaceChildren();
    if (busy) {
      const spinner = document.createElement('span');
      spinner.className = 'mc-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      button.append(spinner, document.createTextNode(` ${label}`));
    } else {
      button.textContent = label;
    }
  }

  function productButtons(form, productRoot) {
    return [...productRoot.querySelectorAll('[data-product-submit]')].filter((button) => button.form === form);
  }

  function setProductBusy(form, productRoot, busy) {
    form.setAttribute('aria-busy', String(busy));
    for (const button of productButtons(form, productRoot)) {
      if (busy) button.dataset.disabledBeforeRequest = String(button.disabled);
      button.disabled = busy || button.dataset.disabledBeforeRequest === 'true';
      button.setAttribute('aria-busy', String(busy));
      if (busy) setButtonLabel(button, productRoot.dataset.labelAdding || button.dataset.defaultLabel || '', true);
    }
  }

  function restoreProductButtons(form, productRoot, token) {
    if (form.dataset.productRequest !== token) return;
    variantState.restoreLabels(productRoot);
    for (const button of productButtons(form, productRoot)) {
      button.removeAttribute('aria-busy');
      delete button.dataset.disabledBeforeRequest;
    }
    form.removeAttribute('aria-busy');
  }

  async function responseJson(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update the cart.');
    return data;
  }

  async function getCart() {
    const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
    return responseJson(response);
  }

  function formatMoney(cents, currency) {
    return variantState.formatMoney(cents, currency, document.documentElement.lang || 'en-US');
  }

  function link(href, label, className) {
    const element = document.createElement('a');
    element.href = href;
    element.textContent = label;
    if (className) element.className = className;
    return element;
  }

  function localizedTemplate(template, title) {
    return String(template || '').replace('__TITLE__', title || '');
  }

  function cartQuantityButton(item, step, template) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.cartQuantityAction = '';
    button.dataset.cartLineKey = item.key;
    button.dataset.cartCurrentQuantity = String(item.quantity);
    button.dataset.cartQuantityStep = String(step);
    button.setAttribute('aria-label', localizedTemplate(template, item.product_title));
    button.textContent = step < 0 ? '−' : '+';
    return button;
  }

  function cartItemElement(item, currency, labels) {
    const row = document.createElement('li');
    row.className = 'mc-cart-drawer__item';

    if (item.image) {
      const imageLink = link(item.url, '');
      imageLink.className = 'mc-cart-drawer__image';
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.product_title || '';
      image.loading = 'lazy';
      imageLink.append(image);
      row.append(imageLink);
    } else {
      row.classList.add('mc-cart-drawer__item--no-image');
    }

    const details = document.createElement('div');
    const line = document.createElement('div');
    line.className = 'mc-cart-drawer__line';
    line.append(link(item.url, item.product_title));
    const price = document.createElement('strong');
    price.textContent = formatMoney(item.final_line_price, currency);
    line.append(price);
    details.append(line);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.textContent = item.variant_title;
      details.append(variant);
    }

    const controls = document.createElement('div');
    controls.className = 'mc-cart-drawer__controls';
    const quantity = document.createElement('div');
    quantity.className = 'mc-qty';
    const count = document.createElement('span');
    count.textContent = String(item.quantity);
    quantity.append(cartQuantityButton(item, -1, labels.decrease), count, cartQuantityButton(item, 1, labels.increase));

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'mc-link-button';
    remove.dataset.cartRemove = '';
    remove.dataset.cartLineKey = item.key;
    remove.setAttribute('aria-label', localizedTemplate(labels.remove, item.product_title));
    remove.textContent = localizedTemplate(labels.remove, item.product_title);
    controls.append(quantity, remove);
    details.append(controls);
    row.append(details);
    return row;
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
    document.querySelectorAll('[data-dialog-open="McCartDrawer"]').forEach((trigger) => {
      const template = count === 1 ? trigger.dataset.cartLabelOne : trigger.dataset.cartLabelOther;
      trigger.setAttribute('aria-label', String(template || '').replace('__COUNT__', String(count)));
    });
  }

  function renderCart(cart) {
    const drawer = document.getElementById('McCartDrawer');
    const content = drawer?.querySelector('[data-cart-drawer-content]');
    if (!content) return;
    showCartDrawerError(false);
    content.replaceChildren();
    updateCartCount(cart.item_count);

    if (!cart.item_count) {
      const empty = document.createElement('div');
      empty.className = 'mc-empty';
      const heading = document.createElement('h3');
      heading.textContent = content.dataset.emptyHeading || '';
      const copy = document.createElement('p');
      copy.textContent = content.dataset.emptyCopy || '';
      empty.append(heading, copy);
      const actions = document.createElement('div');
      actions.className = 'mc-actions';
      const productUrl = content.dataset.emptyProductUrl;
      actions.append(link(productUrl || content.dataset.continueUrl || rootPath, productUrl ? (content.dataset.emptyProductLabel || '') : (content.dataset.continueLabel || ''), productUrl ? 'mc-button' : 'mc-button mc-button--secondary'));
      empty.append(actions);
      content.append(empty);
      return;
    }

    const list = document.createElement('ul');
    list.className = 'mc-list';
    const labels = { remove: content.dataset.removeLabel, decrease: content.dataset.decreaseLabel, increase: content.dataset.increaseLabel };
    cart.items.forEach((item) => list.append(cartItemElement(item, cart.currency, labels)));

    const subtotal = document.createElement('div');
    subtotal.className = 'mc-summary__row';
    const subtotalLabel = document.createElement('span');
    subtotalLabel.textContent = content.dataset.subtotalLabel || '';
    const subtotalValue = document.createElement('strong');
    subtotalValue.textContent = formatMoney(cart.total_price, cart.currency);
    subtotal.append(subtotalLabel, subtotalValue);

    const actions = document.createElement('div');
    actions.className = 'mc-actions';
    actions.append(link(`${rootPath}cart`, content.dataset.viewCartLabel || '', 'mc-button mc-button--secondary'));
    actions.append(link(content.dataset.continueUrl || rootPath, content.dataset.continueLabel || '', 'mc-button mc-button--secondary'));
    const checkoutForm = document.createElement('form');
    checkoutForm.action = `${rootPath}cart`;
    checkoutForm.method = 'post';
    const checkout = document.createElement('button');
    checkout.className = 'mc-button';
    checkout.type = 'submit';
    checkout.name = 'checkout';
    checkout.textContent = content.dataset.checkoutLabel || '';
    checkoutForm.append(checkout);
    actions.append(checkoutForm);
    content.append(list, subtotal, actions);
  }

  function setCartBusy(busy) {
    const drawer = document.getElementById('McCartDrawer');
    if (!drawer) return;
    drawer.setAttribute('aria-busy', String(busy));
    drawer.querySelectorAll('[data-cart-drawer-content] button,[data-cart-retry]').forEach((button) => { button.disabled = busy; });
  }

  function showCartDrawerError(visible) {
    const drawer = document.getElementById('McCartDrawer');
    const error = drawer?.querySelector('[data-cart-drawer-error]');
    if (error) error.hidden = !visible;
  }

  function getCartDrawerController() {
    const drawer = document.getElementById('McCartDrawer');
    if (!cartDrawerState || !headerRuntimeState) return null;
    const content = drawer?.querySelector('[data-cart-drawer-content]');
    const live = drawer?.querySelector('[data-cart-live]');
    const binding = headerRuntimeState.bindCart({ root: drawer, currentRoot: cartDrawerRoot, currentController: cartDrawerController, createController: () => cartDrawerState.create({
      perform: async ({ key, quantity }) => {
        const response = await fetch(`${rootPath}cart/change.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        return responseJson(response);
      },
      onBusy: (busy) => {
        setCartBusy(busy);
        if (busy) setTextStatus(live, content?.dataset.cartUpdating || '');
      },
      onSuccess: (cart) => {
        renderCart(cart);
        showCartDrawerError(false);
        const message = content?.dataset.cartUpdated || '';
        setTextStatus(live, message);
        announce(message);
      },
      onError: () => {
        showCartDrawerError(true);
        const message = drawer?.querySelector('[data-cart-drawer-error] p')?.textContent || '';
        setTextStatus(live, message, true);
        announce(message);
      }
    }) });
    cartDrawerRoot = binding.root;
    cartDrawerController = binding.controller;
    return cartDrawerController;
  }

  function resetHeaderRuntime(scope) {
    const reset = headerRuntimeState?.teardown({ scope, activeDialog, closeDialog, cartController: cartDrawerController, body: document.body });
    cartDrawerController = reset?.cartController || null;
    cartDrawerRoot = reset?.cartRoot || null;
    returnFocus = reset?.returnFocus || null;
    activeDialog = null;
  }

  function changeCartLine(key, quantity) {
    return getCartDrawerController()?.run({ key, quantity });
  }

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-dialog-open]');
    const close = event.target.closest('[data-dialog-close]');
    if (open) { event.preventDefault(); openDialog(document.getElementById(open.dataset.dialogOpen), open); }
    if (close) closeDialog(close.closest('.mc-dialog'));

    const cartQuantity = event.target.closest('[data-cart-quantity-action]');
    const cartRemove = event.target.closest('[data-cart-remove]');
    const cartRetry = event.target.closest('[data-cart-retry]');
    if (cartQuantity) {
      const current = Number(cartQuantity.dataset.cartCurrentQuantity || 0);
      const step = Number(cartQuantity.dataset.cartQuantityStep || 0);
      changeCartLine(cartQuantity.dataset.cartLineKey, Math.max(0, current + step));
    } else if (cartRemove) {
      changeCartLine(cartRemove.dataset.cartLineKey, 0);
    } else if (cartRetry) {
      getCartDrawerController()?.retry();
    }

    const quantity = event.target.closest('[data-quantity-step]');
    if (quantity) {
      const input = document.getElementById(quantity.getAttribute('aria-controls'));
      if (input) {
        const next = Math.max(Number(input.min || 0), Number(input.value || 0) + Number(quantity.dataset.quantityStep));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const accordion = event.target.closest('[data-accordion-trigger]');
    if (accordion) {
      const panel = document.getElementById(accordion.getAttribute('aria-controls'));
      const expanded = accordion.getAttribute('aria-expanded') === 'true';
      accordion.setAttribute('aria-expanded', String(!expanded));
      if (panel) panel.hidden = expanded;
    }

    const recovery = event.target.closest('[data-customer-recovery]');
    if (recovery) {
      const panel = document.getElementById(recovery.getAttribute('aria-controls'));
      if (panel) {
        panel.hidden = false;
        panel.querySelector('input')?.focus();
      }
    }

    const destructive = event.target.closest('[data-confirm-message]');
    if (destructive && !window.confirm(destructive.dataset.confirmMessage || '')) event.preventDefault();
  });

  document.addEventListener('keydown', (event) => {
    if (!activeDialog) return;
    if (event.key === 'Escape') return closeDialog(activeDialog);
    if (event.key !== 'Tab') return;
    const panel = activeDialog.querySelector('.mc-dialog__panel');
    const items = focusableItems(panel);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  function initProductForms(scope = document) {
    scope.querySelectorAll('[data-product-form]').forEach((form) => {
    if (form.dataset.mcInitialized === 'true') return;
    form.dataset.mcInitialized = 'true';
    const productRoot = form.closest('[data-product-root]');
    if (!productRoot || !variantState || !productAddState) return;
    const variantsNode = form.querySelector('[data-product-variants]');
    const variants = variantState.parseVariants(variantsNode);
    const variantControl = form.querySelector('[name="id"]');
    const variantSelect = form.querySelector('select[name="id"]');
    if (variantControl) variantState.apply(productRoot, variantState.findVariant(variants, variantControl.value), formatMoney);
    if (variantSelect) {
      variantSelect.addEventListener('change', () => {
        variantState.apply(productRoot, variantState.findVariant(variants, variantSelect.value), formatMoney);
      });
    }
    const status = form.querySelector('[data-product-status]');
    const fallback = form.querySelector('[data-product-cart-fallback]');
    let submitter = null;
    let token = '';
    const addController = productAddState.create({
      add: async (payload) => {
        const response = await fetch(`${rootPath}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: payload });
        return responseJson(response);
      },
      hydrate: getCart,
      onState: (state, data) => {
        const buttons = productButtons(form, productRoot);
        if (state === 'adding') {
          if (fallback) fallback.hidden = true;
          setTextStatus(status, '');
          setProductBusy(form, productRoot, true);
          variantState.setControlsBusy(form, true);
          return;
        }
        if (state === 'add_error') {
          const errorLabel = productRoot.dataset.labelError || data?.message || '';
          setTextStatus(status, errorLabel, true);
          buttons.forEach((button) => setButtonLabel(button, productRoot.dataset.labelRetry || ''));
          announce(errorLabel);
          variantState.setControlsBusy(form, false);
          buttons.forEach((button) => {
            button.disabled = button.dataset.disabledBeforeRequest === 'true';
            button.removeAttribute('aria-busy');
          });
          form.removeAttribute('aria-busy');
          window.setTimeout(() => restoreProductButtons(form, productRoot, token), 1800);
          return;
        }
        if (state === 'partial_success') {
          const addedLabel = productRoot.dataset.labelCartRefreshError || productRoot.dataset.labelAdded || '';
          const dynamicCheckout = form.querySelector('[data-dynamic-checkout]');
          setTextStatus(status, addedLabel);
          buttons.forEach((button) => {
            setButtonLabel(button, productRoot.dataset.labelAdded || addedLabel);
            button.disabled = true;
            button.removeAttribute('aria-busy');
            delete button.dataset.disabledBeforeRequest;
          });
          form.removeAttribute('aria-busy');
          if (dynamicCheckout) {
            dynamicCheckout.hidden = true;
            dynamicCheckout.setAttribute('aria-hidden', 'true');
          }
          if (fallback) fallback.hidden = false;
          announce(addedLabel);
          return;
        }
        if (state === 'success') {
          renderCart(data);
          const addedLabel = productRoot.dataset.labelAdded || '';
          setTextStatus(status, addedLabel);
          buttons.forEach((button) => setButtonLabel(button, addedLabel));
          announce(addedLabel);
          openDialog(document.getElementById('McCartDrawer'), submitter || buttons[0]);
          variantState.setControlsBusy(form, false);
          buttons.forEach((button) => {
            button.disabled = button.dataset.disabledBeforeRequest === 'true';
            button.removeAttribute('aria-busy');
          });
          form.removeAttribute('aria-busy');
          window.setTimeout(() => restoreProductButtons(form, productRoot, token), 1800);
        }
      }
    });
    form.addEventListener('submit', async (event) => {
      if (!window.fetch) return;
      event.preventDefault();
      token = String(++productRequest);
      form.dataset.productRequest = token;
      submitter = event.submitter;
      const payload = variantState.createPayload(form, FormData);
      await addController.run(payload);
    });
    });
  }

  function initProductGalleries(scope = document) {
    scope.querySelectorAll('[data-product-gallery]').forEach((gallery) => productGallery?.init(gallery));
  }

  function initCartPages(scope = document) {
    scope.querySelectorAll('[data-cart-page-form]').forEach((form) => {
    if (form.dataset.mcInitialized === 'true') return;
    form.dataset.mcInitialized = 'true';
    if (!cartPage || !window.fetch) return;
    let requestPending = false;
    let lastAction = null;
    const run = async (url, body) => {
      if (requestPending) return;
      requestPending = true;
      cartPage.showError(form, '');
      cartPage.setBusy(form, true);
      try {
        await cartPage.requestJson(window.fetch.bind(window), url, body);
        window.location.assign(`${rootPath}cart`);
      } catch (error) {
        cartPage.showError(form, error.message);
        announce(error.message);
      } finally {
        requestPending = false;
        cartPage.setBusy(form, false);
      }
    };
    form.addEventListener('submit', (event) => {
      if (event.submitter?.name !== 'update') return;
      event.preventDefault();
      lastAction = () => run(`${rootPath}cart/update.js`, { updates: cartPage.collectUpdates(form) });
      lastAction();
    });
    form.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-page-remove]');
      const retry = event.target.closest('[data-cart-page-retry]');
      if (remove) {
        event.preventDefault();
        lastAction = () => run(`${rootPath}cart/change.js`, { id: remove.dataset.cartLineKey, quantity: 0 });
        lastAction();
      } else if (retry && lastAction) {
        event.preventDefault();
        lastAction();
      }
    });
    });
  }

  function initContactForms(scope = document) {
    scope.querySelectorAll('[data-contact-form]').forEach((form) => {
    if (form.dataset.mcInitialized === 'true') return;
    form.dataset.mcInitialized = 'true';
    form.addEventListener('submit', () => {
      form.setAttribute('aria-busy', 'true');
      const button = form.querySelector('[data-contact-submit]');
      if (button) {
        button.disabled = true;
        setButtonLabel(button, form.dataset.sendingLabel || '', true);
      }
    });
    });
  }

  function initPolicyControls(scope = document) {
    scope.querySelectorAll('[data-policy-route-select]').forEach((select) => {
      if (select.dataset.mcInitialized === 'true') return;
      select.dataset.mcInitialized = 'true';
      select.addEventListener('change', () => {
        if (select.value) window.location.assign(select.value);
      });
    });
  }

  function initCustomerAddresses(scope = document) { customerAddressState?.init(scope); }

  function initTheme(scope = document) {
    initProductForms(scope);
    initProductGalleries(scope);
    initCartPages(scope);
    initContactForms(scope);
    initPolicyControls(scope);
    initCustomerAddresses(scope);
    const contactStatus = scope.querySelector('[data-contact-status]');
    if (contactStatus) contactStatus.focus();
  }

  initTheme(document);

  document.addEventListener('shopify:section:load', (event) => {
    initTheme(event.target);
    if (event.target.querySelector('[data-site-header]')) {
      resetHeaderRuntime(event.target);
      navigationController?.destroy?.();
      navigationController = navigationState?.init(document);
      getCartDrawerController();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-product-gallery]').forEach((gallery) => productGallery?.destroy?.(gallery));
    if (event.target.querySelector('[data-site-header]')) {
      resetHeaderRuntime(event.target);
      navigationController?.destroy?.();
      navigationController = null;
    }
  });
})();
